import React, { useCallback, useContext, useState } from "react"

import { Link } from "expo-router"
import _ from "lodash"

import { Link as BackendLink, Client, ClientCollection, ClientState, Index, User } from "wealthmode-frontend/backend"
import { DialogContext } from "wealthmode-frontend/context"
import { Button, ButtonText, HStack, Icon, Text } from "wealthmode-frontend/gluestack/ui"
import { AsteriskIcon, BadgeQuestionMarkIcon } from "wealthmode-frontend/icons"

import { Config } from "@/Config"
import DataTable from "@/components/DataTable"
import IDLink from "@/components/IDLink"
import { BackendContext } from "@/context"
import { GoogleIcon } from "@/lib/BrandIcons"
import { CLIENT_STATE_COLORS, CLIENT_STATE_LABELS } from "@/lib/labels"

/**
 * Finds the common domain between two hostnames by comparing from the right.
 * Returns the common domain with a leading dot (e.g., ".example.com") if at least
 * 2 parts match, or null if no valid common domain is found.
 */
function getCommonDomain(hostname1: string, hostname2: string): string {
  // Find the common domain by comparing from the right
  const parts1 = hostname1.split(".").reverse()
  const parts2 = hostname2.split(".").reverse()
  const commonParts: string[] = []

  for (let i = 0; i < Math.min(parts1.length, parts2.length); i++) {
    if (parts1[i] === parts2[i]) {
      commonParts.unshift(parts1[i])
    } else {
      break
    }
  }

  // Need at least 2 parts for a valid domain (e.g., "example.com")
  if (commonParts.length < 2) {
    throw new Error("No common domain found")
  }

  return commonParts.join(".")
}

// The state a row's button moves the client to, and what that button says.
const CLIENT_STATE_ACTIONS: Record<ClientState, { label: string; next: ClientState }> = {
  pending: { label: "Activate", next: "active" },
  active: { label: "Disable", next: "disabled" },
  disabled: { label: "Enable", next: "active" },
}

export default function Clients() {
  const backend = useContext(BackendContext)

  const dialog = useContext(DialogContext)

  const [impersonateLoading, setImpersonateLoading] = useState<Record<string, boolean>>({})
  const [stateLoading, setStateLoading] = useState<Record<string, boolean>>({})
  const [refreshKey, setRefreshKey] = useState(0)

  const load = useCallback(async () => {
    var clients = backend.collection("clients") as ClientCollection
    await clients.fetch({ reload: true })

    return clients
  }, [backend])

  async function setClientState(client: Client, next: ClientState) {
    const selfLink = client.link("self")
    if (selfLink == null) {
      throw new Error("Client has no self link")
    }

    const response = await selfLink.send({
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state: next }),
    })

    if (!response.ok) {
      throw new Error(`Failed to update client state: ${response.status} ${response.statusText}`)
    }
  }

  function confirmStateChange(client: Client, action: { label: string; next: ClientState }) {
    dialog.display({
      title: `${action.label} client ${client.attributes.id}?`,
      submitText: action.label,
      submitType: action.next === "disabled" ? "alert" : "info",
      content: `This sets the client's status to ${CLIENT_STATE_LABELS[action.next]}.`,
      onSubmit: async () => {
        setStateLoading((prev) => ({ ...prev, [client.attributes.id]: true }))

        try {
          await setClientState(client, action.next)
          setRefreshKey((k) => k + 1)
        } catch (error) {
          console.error("Failed to update client state:", error)
          alert(error)
        } finally {
          setStateLoading((prev) => _.omit(prev, [client.attributes.id]))
        }
      },
    })
  }

  async function impersonate(client: Client) {
    const auth = backend.link("auth")?.resolve() as Index | null
    if (!auth) {
      throw new Error("Auth not found")
    }
    await auth.fetch()

    const admin = auth.link("admin")?.resolve() as Index | null
    if (!admin) {
      throw new Error("Admin not found")
    }
    await admin.fetch()

    const impersonateLink = admin.link("impersonate") as BackendLink | null
    if (!impersonateLink) {
      throw new Error("Impersonate not found")
    }

    const user = (client.embed("users") as User[])?.[0]
    if (!user) {
      throw new Error("Client has no users, cannot impersonate")
    }

    const user_id = user.id

    impersonateLink
      .send({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user_id,
        }),
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to impersonate")
        }

        return response.json()
      })
      .then((data) => {
        const token = data.token
        if (!token) {
          throw new Error("Token not found")
        }

        const appUrl = new URL(Config.data.app.web.base_url)
        const thisUrl = new URL(window.location.href)

        const commonDomain = getCommonDomain(appUrl.hostname, thisUrl.hostname)
        document.cookie = `impersonation_token=${token}; path=/; domain=${commonDomain}; max-age=${60 * 5}; secure; samesite=strict`

        window.open(new URL("/auth/impersonate", appUrl.toString()).toString(), "_blank")
      })
      .catch((error) => {
        console.error("Failed to impersonate:", error)
        alert("Failed to impersonate: " + error.message)
      })
  }

  return (
    <DataTable
      title={"Clients"}
      load={load}
      refreshKey={refreshKey}
      header={[
        { id: "id", title: "ID", shrink: true },
        { id: "created", title: "Created", shrink: true },
        { id: "state", title: "Status", shrink: true },
        { id: "user", title: "Primary User" },
        { id: "links", title: "Links", noTruncate: true },
        { id: "actions", title: "Actions", noTruncate: true },
      ]}
      render={(model: Client, field: string) => {
        switch (field) {
          case "id":
            return (
              <IDLink
                href={`/clients/${model.attributes.id}`}
                id={model.attributes.id}
              />
            )
          case "created":
            return (
              <Text className="text-foreground-secondary">
                {new Date(model.attributes.created * 1000).toLocaleString()}
              </Text>
            )
          case "state":
            const state = model.attributes.state
            const stateAction = CLIENT_STATE_ACTIONS[state]

            return (
              <HStack className="gap-4 items-center">
                <Text className={CLIENT_STATE_COLORS[state] ?? "text-foreground-secondary"}>
                  {CLIENT_STATE_LABELS[state] ?? state}
                </Text>
                {stateAction == null ? null : (
                  <Button
                    size="xs"
                    action="secondary"
                    disabled={stateLoading[model.attributes.id]}
                    onPress={() => confirmStateChange(model, stateAction)}
                  >
                    <ButtonText>{stateLoading[model.attributes.id] ? "Saving..." : stateAction.label}</ButtonText>
                  </Button>
                )}
              </HStack>
            )
          case "user":
            const namedUser = _.find(model.embed("users") as User[], (user) => user.hasName())
            const providerIcons = _.map(model.embed("users") as User[], (user) => {
              if (user.attributes.provider == "google-oauth2") {
                return GoogleIcon
              } else if (user.attributes.provider == "password") {
                return AsteriskIcon
              }
              return BadgeQuestionMarkIcon
            })

            return (
              <HStack className="gap-4 items-center">
                <Text
                  key="name"
                  className="text-foreground-secondary"
                >
                  {namedUser?.attributes.name || "Unknown"}
                </Text>
                <HStack className="gap-2">
                  {providerIcons.map((icon, i) => (
                    <Icon
                      key={`provider-${i}`}
                      as={icon}
                      className="inline shrink-0 text-foreground-tertiary h-8 w-8"
                    />
                  ))}
                </HStack>
              </HStack>
            )
          case "links":
            return (
              <HStack className="gap-4">
                <Link href={`/clients/${model.attributes.id}/accounts`}>
                  <Text className="text-markdown-link hover:opacity-80 underline">Accounts</Text>
                </Link>
                <Link href={`/clients/${model.attributes.id}/users`}>
                  <Text className="text-markdown-link hover:opacity-80 underline">Users</Text>
                </Link>
              </HStack>
            )
          case "actions":
            return (
              <Button
                size="sm"
                action="primary"
                disabled={impersonateLoading[model.attributes.id]}
                onPress={() => {
                  setImpersonateLoading((prev) => ({ ...prev, [model.attributes.id]: true }))
                  impersonate(model)
                    .catch((error) => {
                      alert(error)
                    })
                    .finally(() => {
                      setImpersonateLoading((prev) => _.omit(prev, [model.attributes.id]))
                    })
                }}
                className="bg-background-contrast"
              >
                <ButtonText className="text-foreground-contrast">
                  {impersonateLoading[model.attributes.id] ? "Loading..." : "Impersonate"}
                </ButtonText>
              </Button>
            )
        }
        return <Text className="text-foreground-secondary">{model.get(field)}</Text>
      }}
    />
  )
}
